import React, { useState } from 'react'
import { Input, Modal } from 'antd'
import ProductTwoPanelSelector from './ProductTwoPanelSelector'
import DefaultProductImage from './DefaultProductImage'

interface Product {
  name?: string
  code?: string
  image?: string | null
  label?: string
  value?: string
}

interface ProductSelectInputProps {
  selectedProducts: Product[]
  onProductSelect: (products: Product[]) => void
  products: Product[]
  loading: boolean
  hasMore: boolean
  onLoadMore: () => void
  onSearch: (keyword: string) => void
  operator?: string
  disabled?: boolean
}

// Style constants to avoid inline style repetition
const STYLES = {
  selectedContainer: {
    base: {
      border: '1px solid #d9d9d9',
      borderRadius: '2px',
      padding: '8px',
      transition: 'all 0.3s',
    },
    enabled: {
      backgroundColor: '#fafafa',
      cursor: 'pointer',
      opacity: 1,
    },
    disabled: {
      backgroundColor: '#f5f5f5',
      cursor: 'not-allowed',
      opacity: 0.6,
    },
    hover: {
      borderColor: '#40a9ff',
      boxShadow: '0 0 0 2px rgba(24, 144, 255, 0.2)',
    },
    default: {
      borderColor: '#d9d9d9',
      boxShadow: 'none',
    },
  },
  countText: {
    marginBottom: '8px',
    color: '#8c8c8c',
    fontSize: '12px',
  },
  productItem: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: '4px',
  },
  productImage: {
    width: '24px',
    height: '24px',
    objectFit: 'cover' as const,
    borderRadius: '2px',
    display: 'block',
  },
  productImageContainer: {
    marginRight: '8px',
    flexShrink: 0,
  },
  productName: {
    flex: 1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap' as const,
    fontSize: '12px',
    textAlign: 'left' as const,
  },
  moreText: {
    fontSize: '12px',
    color: '#8c8c8c',
    marginTop: '4px',
  },
} as const

const ProductSelectInput: React.FC<ProductSelectInputProps> = ({
  selectedProducts,
  onProductSelect,
  products,
  loading,
  hasMore,
  onLoadMore,
  onSearch,
  operator,
  disabled = false,
}) => {
  const [visible, setVisible] = useState(false)

  const handleSelect = (products: Product[]) => {
    onProductSelect(products)
    setVisible(false)
  }

  const handleInputClick = () => {
    if (!disabled) {
      setVisible(true)
    }
  }

  return (
    <>
      {selectedProducts.length > 0 ? (
        <div 
          style={{
            ...STYLES.selectedContainer.base,
            ...(disabled ? STYLES.selectedContainer.disabled : STYLES.selectedContainer.enabled),
          }}
          onClick={handleInputClick}
          onMouseEnter={(e) => {
            if (!disabled) {
              Object.assign(e.currentTarget.style, STYLES.selectedContainer.hover);
            }
          }}
          onMouseLeave={(e) => {
            if (!disabled) {
              Object.assign(e.currentTarget.style, STYLES.selectedContainer.default);
            }
          }}
        >
          <div style={STYLES.countText}>
            {selectedProducts.length} selected
          </div>
          {selectedProducts.slice(0, 3).map((product, index) => (
            <div 
              key={product.code || product.value} 
              style={{ 
                ...STYLES.productItem,
                marginBottom: index < 2 ? '4px' : 0
              }}
            >
              <div style={STYLES.productImageContainer}>
                {product.image ? (
                  <img 
                    src={product.image} 
                    alt={product.name || product.label}
                    style={STYLES.productImage}
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = 'none'
                    }}
                  />
                ) : (
                  <DefaultProductImage size={24} />
                )}
              </div>
              <div style={STYLES.productName}>
                {product.name || product.label}
              </div>
            </div>
          ))}
          {selectedProducts.length > 3 && (
            <div style={STYLES.moreText}>
              and {selectedProducts.length - 3} more...
            </div>
          )}
        </div>
      ) : (
        <Input 
          placeholder={disabled ? "Please select operator first" : "Select products..."}
          readOnly
          disabled={disabled}
          style={{ cursor: disabled ? 'not-allowed' : 'pointer' }}
          onClick={handleInputClick}
        />
      )}
      
      <Modal
        title="Select Products"
        visible={visible}
        onCancel={() => setVisible(false)}
        footer={null}
        width="80%"
        style={{ maxWidth: '900px' }}
        bodyStyle={{ padding: 0 }}
        destroyOnClose
      >
        <ProductTwoPanelSelector
          onSelect={handleSelect}
          selectedProducts={selectedProducts}
          products={products}
          loading={loading}
          hasMore={hasMore}
          onLoadMore={onLoadMore}
          onSearch={onSearch}
          operator={operator}
          onCancel={() => setVisible(false)}
        />
      </Modal>
    </>
  )
}

export default ProductSelectInput